import logoRecommended from '../../assets/review/recommended.png';
import logoNotRecommended from '../../assets/review/not-recommended.png';
import React from 'react';

export const formatReviewText = (text: string): string => {
    const translationIndex = text.indexOf('(Translated by Google)');
    return translationIndex !== -1 ? text.substring(0, translationIndex).trim() : text;
};

export const truncateName = (name: string): string => {
    return name.trim().split(' ')[0];
};

export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
};

export const formatDate = (dateString: string): string => {
    return new Intl.DateTimeFormat('fr-FR', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    }).format(new Date(dateString));
};

export const getRatingNumber = (rating: any): number => {
    if (typeof rating === 'number') return rating;

    const ratingMap: Record<string, number> = {
        FIVE: 5,
        FOUR: 4,
        THREE: 3,
        TWO: 2,
        ONE: 1,
        ZERO: 0,
    };

    return ratingMap[rating.toUpperCase()] || 0;
};

type RecommendationType = 'positive' | string;

interface RecommendationConfig {
    textColor: string;
    fontSize: string;
    gap: string;
    justify?: string;
    imageHeight: string;
    imageWidth?: string;
}

const TEMPLATE_CONFIGS: Record<string, RecommendationConfig> = {
    template1: {
        textColor: 'text-white',
        fontSize: 'text-[40px]',
        gap: 'gap-5',
        imageHeight: 'h-10',
        imageWidth: 'w-auto',
    },
    template2: {
        textColor: 'text-black',
        fontSize: 'text-[40px]',
        gap: 'gap-5',
        justify: 'justify-center',
        imageHeight: 'h-10',
    },
    template3: {
        textColor: 'text-white',
        fontSize: 'text-[40px]',
        gap: 'gap-5',
        justify: 'justify-center',
        imageHeight: 'h-10',
    },
    template4: {
        textColor: 'text-white',
        fontSize: 'text-[40px]',
        gap: 'gap-3',
        imageHeight: 'h-10',
    },
    template5: {
        textColor: 'text-white',
        fontSize: 'text-[40px]',
        gap: 'gap-3',
        imageHeight: 'h-10',
    },
    default: {
        textColor: 'text-gray',
        fontSize: 'text-more-small',
        gap: '',
        imageHeight: '',
    },
};

const RecommendationDisplay: React.FC<{
    isPositive: boolean;
    config: RecommendationConfig;
}> = ({ isPositive, config }) => {
    const logo = isPositive ? logoRecommended : logoNotRecommended;
    const text = isPositive ? 'recommande' : 'ne recommande pas';

    if (config === TEMPLATE_CONFIGS.default) {
        return (
            <>
                <img src={logo} alt={text} />
                <span className={`${config.fontSize} ${config.textColor} pl-1`}>{text}</span>
            </>
        );
    }

    const containerClasses = ['inline-flex', 'items-center', config.gap, config.justify]
        .filter(Boolean)
        .join(' ');

    const imageClasses = [config.imageHeight, config.imageWidth].filter(Boolean).join(' ');

    return (
        <div className={containerClasses}>
            <img src={logo} className={imageClasses} alt={text} crossOrigin="anonymous" />
            <span
                className={`${config.justify ? 'justify-start' : ''} ${config.textColor} ${config.fontSize} font-normal font-['Montserrat'] leading-[42px]`}
            >
                {text}
            </span>
        </div>
    );
};

export const formatRecommendationDisplay = (
    type: RecommendationType,
    templateName: string = '',
): JSX.Element => {
    const isPositive = type === 'positive';
    const config = TEMPLATE_CONFIGS[templateName] || TEMPLATE_CONFIGS.default;

    return <RecommendationDisplay isPositive={isPositive} config={config} />;
};
